import React, { useMemo } from 'react';
import { fmtCurrency, fmtNumber, fmtPercent, fmtRatio } from '../../shared/format';
import { KpiCard, getComparisonLabel } from '../shared';
import InfoTip from '../../shared/InfoTip';
import SelectMenu from '../../shared/SelectMenu';

const COMPARISON_OPTIONS = [
  { value: 'previous_period', label: 'Previous Period' },
  { value: 'previous_year', label: 'Previous Year' },
  { value: 'none', label: 'None' },
];

const ComparisonSelector = ({ comparisonMode, setComparisonMode }) => (
  <div className="flex items-center gap-1.5 max-[344px]:w-full">
    <InfoTip text="Shows the % change on each KPI card by comparing the current date range against the selected period." />
    <span className="text-xs text-gray-500 dark:text-gray-400">Compare:</span>
    <SelectMenu
      options={COMPARISON_OPTIONS}
      value={comparisonMode}
      onChange={setComparisonMode}
      ariaLabel="Comparison period"
      className="text-xs max-[344px]:ml-auto"
    />
  </div>
);

const KpiCards = ({
  kpis, loading, accountTrend, bookingsTrend,
  comparisonMode, setComparisonMode,
  rangeStart, rangeEnd,
  isPartialPeriod, dataThrough,
}) => {
  const comp = kpis?.comparison;

  const dataThroughLabel = useMemo(
    () => (dataThrough
      ? new Date(`${dataThrough}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      : null),
    [dataThrough],
  );

  const sparkSpend = useMemo(() => accountTrend?.map((datum) => ({ v: datum.cost })) ?? [], [accountTrend]);
  const sparkConversions = useMemo(() => accountTrend?.map((datum) => ({ v: datum.all_conversions })) ?? [], [accountTrend]);
  const sparkClicks = useMemo(() => accountTrend?.map((datum) => ({ v: datum.clicks })) ?? [], [accountTrend]);
  const sparkCtr = useMemo(() => accountTrend?.map((datum) => ({ v: datum.clicks / (datum.impressions || 1) })) ?? [], [accountTrend]);
  const sparkRoas = useMemo(() => accountTrend?.map((datum) => ({ v: datum.conversion_value ? datum.conversion_value / (datum.cost || 1) : 0 })) ?? [], [accountTrend]);

  // Bookings sparklines use the unfiltered bookingsTrend, which carries the per-day
  // tier1_bookings_estimated field (account-wide, matching the Bookings KPI number).
  // The field is a float and can be null (campaign-filtered) or 0 (no bookings that day),
  // so guard the cost-per-booking division against NaN/Infinity.
  //
  // 2026-07-29 — `fn_get_gads_spend_trend` returns per-day rows without any
  // tier1_bookings_estimated column at all (verified: keys returned are
  // cost/date/clicks/impressions/all_conversions/conversion_value). The
  // BOOKINGS KPI number (kpis.tier1_bookings) IS accurate — it comes from
  // the summary RPC — but the per-day series doesn't include it. Without
  // that field every sparkline datum evaluates to 0 → a flat line at the
  // bottom of the card that reads as "no data" and looks broken. Filed as
  // a backend follow-up; meanwhile: if the series has no positive signal,
  // suppress the sparkline entirely so the card shows just the number
  // (accurate) instead of a misleading flat line. This auto-restores the
  // sparkline the moment backend adds the column back.
  //
  // 2026-08-04 — re-verified still missing. The per-day data DOES exist in
  // `gads_conversion_actions_daily` (category=PURCHASE, status=ENABLED,
  // summed by date it matches kpis.tier1_bookings exactly: 150.67 for Verde
  // Jun 30–Jul 29), but that table is RLS-blocked for authenticated users
  // (same 42501 as getGoogleAdsLastUpdated hit) so the frontend can't read
  // it directly — backend must expose it via the trend RPC or a new
  // SECURITY DEFINER fn. Same table would also unlock the Phone Calls
  // sparkline (PHONE_CALL_LEAD rows). Escalated in the backend follow-ups
  // doc; per Charles 2026-08-04, keep the working sparklines in the
  // meantime rather than hiding them row-wide.
  const sparkBookings = useMemo(() => {
    const rows = bookingsTrend?.map((datum) => ({ v: Number(datum.tier1_bookings_estimated) || 0 })) ?? [];
    return rows.some((r) => r.v > 0) ? rows : [];
  }, [bookingsTrend]);
  const sparkCostPerBooking = useMemo(() => {
    const rows = bookingsTrend?.map((datum) => {
      const bookings = Number(datum.tier1_bookings_estimated) || 0;
      return { v: bookings > 0 ? datum.cost / bookings : 0 };
    }) ?? [];
    return rows.some((r) => r.v > 0) ? rows : [];
  }, [bookingsTrend]);

  // Google assigns fractional conversion credit, so tier1_bookings is rarely a
  // whole number (e.g. 121.67). Show the exact 2-decimal figure: Cost Per Booking
  // divides spend by this value, and a rounded count breaks the on-screen math
  // ($2,952.91 ÷ 122 = $24.20, but the card says $24.27). fmtNumber's
  // forceDecimals only renders 1 decimal, which still misses by a cent.
  const fmtBookings = (n) => {
    const num = Number(n);
    return num % 1 === 0
      ? Math.round(num).toLocaleString()
      : num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const comparisonEmpty = comparisonMode !== 'none' && comp != null &&
    [comp.spend_change_pct, comp.bookings_change_pct, comp.cost_per_booking_change_pct,
     comp.roas_change_pct, comp.phone_calls_change_pct, comp.all_conversions_change_pct,
     comp.clicks_change_pct, comp.ctr_change_pct].every(value => value === null || value === undefined);
  const comparisonNoData = comparisonMode !== 'none' && !loading && kpis && (!comp || comparisonEmpty);

  const label = getComparisonLabel(comparisonMode, rangeStart, rangeEnd);

  return (
    <>
      {/* Primary KPI row */}
      <div>
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <div>
            <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 font-semibold">What Matters Most</div>
          </div>
          <div className="flex items-center gap-3 max-[344px]:w-full">
            <ComparisonSelector comparisonMode={comparisonMode} setComparisonMode={setComparisonMode} />
          </div>
        </div>
        {(label || (isPartialPeriod && dataThroughLabel)) && (
          <div className="text-[13px] text-gray-400 dark:text-gray-500 mb-3 -mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5">
            {label ? <span>{label}</span> : <span>Data through {dataThroughLabel}</span>}
            {isPartialPeriod && dataThroughLabel && (
              <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400">
                (period so far)
                <InfoTip text={`Data is only in through ${dataThroughLabel}, so the current period stops there and the comparison spans the same number of days — keeping the % change like-for-like instead of pitting a partial period against a full one.`} />
              </span>
            )}
            {comparisonNoData && (
              <span className="text-amber-500 dark:text-amber-400">
                — No data available for the comparison period
              </span>
            )}
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <KpiCard
            title="TOTAL SPEND"
            value={kpis ? fmtCurrency(kpis.spend) : '$0.00'}
            subtitle="Ad spend this period"
            changePct={comp?.spend_change_pct ?? null}
            direction="neutral"
            variant="primary"
            icon="dollar"
            loading={loading}
            tooltip="Total amount spent on Google Ads in the selected range. Not good or bad on its own — pair with ROAS or Cost Per Booking to read it."
            sparkData={sparkSpend}
          />
          <KpiCard
            title="BOOKINGS"
            value={kpis ? fmtBookings(kpis.tier1_bookings) : '0'}
            subtitle="Reservations from ads"
            changePct={comp?.bookings_change_pct ?? null}
            direction="higher_good"
            variant="success"
            icon="booking"
            loading={loading}
            tooltip="Actual reservations attributed to Google Ads. Phone calls, form fills, and other lead actions are counted separately under Phone Calls and All Conversions."
            valueTooltip="Google splits conversion credit across campaigns and channels, so bookings can be fractional. This exact figure is what Cost Per Booking divides by — Total Spend ÷ Bookings reconciles to the cent."
            sparkData={sparkBookings}
          />
          <KpiCard
            title="COST PER BOOKING"
            value={kpis ? fmtCurrency(kpis.cost_per_booking) : '$0.00'}
            subtitle="Spend per reservation"
            changePct={comp?.cost_per_booking_change_pct ?? null}
            direction="lower_good"
            variant="subtle"
            icon="dollar"
            loading={loading}
            tooltip={"Average dollars of ad spend needed to produce one booking. Watch the trend, not the absolute number — what counts as good depends on your average reservation value."}
            sparkData={sparkCostPerBooking}
          />
          <KpiCard
            title="RETURN ON AD SPEND"
            value={kpis ? fmtRatio(kpis.roas) : '0.00x'}
            subtitle={kpis?.roas_unattributed_value > 0
              ? `Revenue per $1 spent · +${fmtCurrency(kpis.roas_unattributed_value)} unattributed`
              : 'Revenue per $1 spent'}
            changePct={comp?.roas_change_pct ?? null}
            direction="higher_good"
            variant="primary"
            icon="target"
            loading={loading}
            tooltip={"Return on ad spend — booking revenue divided by ad cost. Above 1.00× means revenue exceeds spend; the threshold for profitable depends on your margins."}
            valueTooltip={kpis?.roas_unattributed_value > 0
              ? `Booking revenue divided by ad cost for the whole range. Uses period totals, not an average of daily ratios. Google couldn't tie ${fmtCurrency(kpis.roas_unattributed_value)} of purchase value to a specific campaign (cross-device, view-through, direct) — those are surfaced separately as "unattributed" rather than folded into the ROAS ratio.`
              : "Booking revenue divided by ad cost for the whole range. Uses period totals, not an average of daily ratios."}
            sparkData={sparkRoas}
          />
        </div>
      </div>

      {/* Supporting KPI row */}
      <div>
        <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3 font-semibold">Supporting Metrics</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {/* No sparkline on this card: it used to render sparkSpend, i.e. a daily
              *cost* trend under a call-count metric, which read as the phone-call
              trend and was simply wrong. fn_get_gads_spend_trend exposes no per-day
              phone-call column (tier2_phone_calls is a range total only), so there is
              nothing correct to plot until the backend adds one. Omitting sparkData
              renders the card chartless — KpiCard guards on sparkData?.length > 1. */}
          <KpiCard
            title="PHONE CALLS"
            value={kpis ? fmtNumber(kpis.tier2_phone_calls) : '0'}
            subtitle="Calls from ads"
            changePct={comp?.phone_calls_change_pct ?? null}
            direction="higher_good"
            variant="muted"
            icon="phone"
            loading={loading}
            tooltip="Calls generated by your ads. These are high-intent leads, but they're not bookings yet — sales still has to close them."
          />
          <KpiCard
            title="ALL CONVERSIONS"
            value={kpis ? fmtNumber(kpis.all_conversions, true) : '0'}
            subtitle="All tracked actions"
            changePct={comp?.all_conversions_change_pct ?? null}
            direction="higher_good"
            variant="muted"
            icon="conversions"
            loading={loading}
            tooltip="Every conversion action across all tiers — bookings, phone calls, form fills, pageviews, and any other goals you've configured. Broadest possible count."
            sparkData={sparkConversions}
          />
          <KpiCard
            title="CLICKS"
            value={kpis ? fmtNumber(kpis.clicks) : '0'}
            subtitle="Ad clicks to website"
            changePct={comp?.clicks_change_pct ?? null}
            direction="higher_good"
            variant="muted"
            icon="click"
            loading={loading}
            tooltip="Times someone clicked on an ad and landed on the site. Click volume × CTR tells you how much budget you can absorb at your current targeting."
            sparkData={sparkClicks}
          />
          <KpiCard
            title="CLICK-THROUGH RATE"
            value={kpis ? fmtPercent(kpis.ctr) : '0.00%'}
            subtitle="Impressions to clicks"
            changePct={comp?.ctr_change_pct ?? null}
            direction="higher_good"
            variant="subtle"
            icon="percent"
            loading={loading}
            tooltip="Click-through rate — share of ad impressions that turned into clicks. A low CTR means the ad isn't compelling for the audience seeing it, or the targeting is off."
            valueTooltip="Total clicks divided by total impressions across the range, then × 100."
            sparkData={sparkCtr}
          />
        </div>
      </div>
    </>
  );
};

export default KpiCards;
